import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authFetch } from "../../lib/api";

// ─── Thunks ────────────────────────────────────────────────────────────────

export const fetchLeaveReport = createAsyncThunk(
  "leaveReport/fetch",
  async ({ year, page = 1, limit = 5, search = "" }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ year, page, limit });
      if (search) params.set("search", search);
      const res = await authFetch(`/api/reports/leave?${params}`);
      const json = await res.json();
      if (!res.ok)
        return rejectWithValue(json?.message || "Failed to fetch leave report");
      return json.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

// ─── Slice ─────────────────────────────────────────────────────────────────

const leaveReportSlice = createSlice({
  name: "leaveReport",
  initialState: {
    data: null, // full API response object
    isLoading: false,
    error: null,
    // local UI state kept here for convenience
    selectedYear: String(new Date().getFullYear()),
    page: 1,
    search: "",
  },
  reducers: {
    setSelectedYear(state, action) {
      state.selectedYear = action.payload;
      state.page = 1;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setSearch(state, action) {
      state.search = action.payload;
      state.page = 1;
    },
    clearLeaveReport(state) {
      state.data = null;
      state.error = null;
      state.page = 1;
      state.search = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaveReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeaveReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchLeaveReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const { setSelectedYear, setPage, setSearch, clearLeaveReport } =
  leaveReportSlice.actions;

// ─── Selectors ─────────────────────────────────────────────────────────────

export const selectLeaveReport = (s) => s.leaveReport.data;
export const selectIsLoadingLeave = (s) => s.leaveReport.isLoading;
export const selectLeaveError = (s) => s.leaveReport.error;
export const selectLeaveYear = (s) => s.leaveReport.selectedYear;
export const selectLeavePage = (s) => s.leaveReport.page;
export const selectLeaveSearch = (s) => s.leaveReport.search;

export default leaveReportSlice.reducer;
